import { useCallback, useState } from 'react';
import { ArrowLeft, Package, Plus, Trash, Warning } from 'shared/icons';
import api from '../../../services/api';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { Mono } from '../../ui/Mono';
import BlockTower from './BlockTower';
import {
    BigAction,
    CodeCard,
    CodeLine,
    FailureScreen,
    PlainAddress,
    StepShell,
    SuccessScreen,
} from './CollectorChrome';
import ScanInput from './ScanInput';
import { PlayMovementDone, PlayScanError, PlayScanSuccess } from './collectorFeedback';
import { FormatLocationPlain, ParseScanResult, SCAN_TYPE_BLOCK, SCAN_TYPE_LOCATION, SCAN_TYPE_LPN } from './collectorScan';

/**
 * ENTRADA DE ESTOQUE (M10) — transformar a etiqueta em branco em pallet.
 *
 * Base: *"ele bipa o LPN, bipa o EAN e informa a quantidade… depois bipa o
 * A-01 e a tela de em qual posição vc vai colocar aparece"* (dono, 14/08).
 *
 * ── r2 (uso como entrada de recebimento) ────────────────────────────────────
 *   - MULTI-ITENS: dá pra dar entrada de N itens (produto + qtd) no MESMO LPN
 *     antes de posicionar — o operador adiciona um, adiciona outro, e só então
 *     vai pra posição. O saldo dos N nasce junto quando o LPN é colocado.
 *   - POSIÇÃO por tipo: se bipar o BLOCO do rack, aparece a TORRE (escolhe a posição
 *     dentro do bloco); se bipar uma posição NÃO-RACK (doca, staging…), coloca
 *     DIRETO ali — o código já é a posição, não tem torre.
 *   - LPN impresso COM itens declarados (fusão imprimir/receber) pula produto/
 *     qtd e só pede a posição; a colocação materializa o conteúdo declarado.
 *
 * O empate de EAN (mesmo código em 2 produtos, ex.: unidade × caixa) sempre
 * PERGUNTA qual é — nunca "escolhe o primeiro".
 */
let itemKeySeed = 0;

const INITIAL_STATE = {
    phase: 'LPN',
    lpnCode: null,
    declared: false,
    declaredItems: [],
    product: null,
    candidates: [],
    qty: '',
    // Itens já dados de entrada neste LPN (fluxo em branco). Cada um: {key, product, qty}.
    items: [],
    block: null,
    positions: [],
    error: null,
    done: null,
};

const IntakeFlow = ({ label, operatorCode, onExit }) => {
    const [state, setState] = useState(INITIAL_STATE);
    const [busy, setBusy] = useState(false);

    const fail = useCallback((error) => {
        PlayScanError();
        setState((current) => ({ ...current, error }));
    }, []);

    const restart = useCallback(() => {
        setState(INITIAL_STATE);
    }, []);

    // ── 1. o LPN do rolo ────────────────────────────────────────────────────
    const handleLpnScan = useCallback(
        async (code) => {
            setBusy(true);
            try {
                const parsed = ParseScanResult(await api.scanWarehouseCode(code));
                if (parsed.type !== SCAN_TYPE_LPN || !parsed.lpn) {
                    fail({
                        title: label('wms.intake.not_an_lpn'),
                        message: label('wms.intake.not_an_lpn_help'),
                        read: code,
                    });
                    return;
                }
                // Etiqueta já usada para na hora, com o código à vista.
                if ((parsed.items || []).length > 0) {
                    fail({
                        title: label('wms.intake.already_used'),
                        message: label('wms.intake.already_used_help'),
                        read: parsed.lpn.code,
                    });
                    return;
                }
                PlayScanSuccess();
                // Rolo impresso COM itens declarados: o conteúdo já é conhecido —
                // pula produto/qtd e vai direto pedir a posição (a colocação
                // materializa os declarados no destino).
                const declaredItems = parsed.lpn.declared_items || [];
                if (declaredItems.length > 0) {
                    setState({
                        ...INITIAL_STATE,
                        phase: 'BLOCK',
                        lpnCode: parsed.lpn.code,
                        declared: true,
                        declaredItems,
                    });
                    return;
                }
                setState({ ...INITIAL_STATE, phase: 'PRODUCT', lpnCode: parsed.lpn.code });
            } catch (error) {
                fail({ title: label('wms.collector.error_generic'), message: error?.message });
            } finally {
                setBusy(false);
            }
        },
        [fail, label],
    );

    // ── 2. o EAN (ou SKU) ───────────────────────────────────────────────────
    const handleProductScan = useCallback(
        async (code) => {
            setBusy(true);
            try {
                const scan = await api.scanWarehouseProduct(code);
                if (scan.status === 'NOT_FOUND') {
                    fail({
                        title: label('wms.intake.product_not_found'),
                        message: label('wms.intake.product_not_found_help'),
                        read: code,
                    });
                    return;
                }
                if (scan.status === 'AMBIGUOUS') {
                    // NÃO escolhe: manda a lista para a tela perguntar.
                    PlayScanError();
                    setState((current) => ({ ...current, phase: 'PICK_PRODUCT', candidates: scan.candidates, error: null }));
                    return;
                }
                PlayScanSuccess();
                setState((current) => ({ ...current, phase: 'QTY', product: scan.product, error: null }));
            } catch (error) {
                fail({ title: label('wms.collector.error_generic'), message: error?.message });
            } finally {
                setBusy(false);
            }
        },
        [fail, label],
    );

    const pickProduct = useCallback((product) => {
        PlayScanSuccess();
        setState((current) => ({ ...current, phase: 'QTY', product, candidates: [], error: null }));
    }, []);

    // ── 3. a quantidade → ADICIONA o item à lista e volta pra revisão ────────
    const confirmQty = useCallback(() => {
        const parsed = Number(String(state.qty).replace(',', '.'));
        if (!Number.isFinite(parsed) || parsed <= 0) {
            fail({ title: label('wms.intake.qty_invalid'), message: label('wms.intake.qty_invalid_help') });
            return;
        }
        PlayScanSuccess();
        itemKeySeed += 1;
        setState((current) => ({
            ...current,
            phase: 'ITEMS',
            items: [...current.items, { key: `item-${itemKeySeed}`, product: current.product, qty: String(parsed) }],
            product: null,
            qty: '',
            candidates: [],
            error: null,
        }));
    }, [fail, label, state.qty]);

    const addAnotherItem = useCallback(() => {
        setState((current) => ({ ...current, phase: 'PRODUCT', product: null, qty: '', candidates: [], error: null }));
    }, []);

    const removeItem = useCallback((key) => {
        setState((current) => ({ ...current, items: current.items.filter((item) => item.key !== key) }));
    }, []);

    const goToPosition = useCallback(() => {
        setState((current) => ({ ...current, phase: 'BLOCK', error: null }));
    }, []);

    // ── Colocar o LPN numa posição (torre OU direto) ────────────────────────
    const placeLpnAt = useCallback(
        async (locationCode, position) => {
            setBusy(true);
            try {
                let lpn;
                if (state.declared) {
                    // LPN com conteúdo DECLARADO: colocar = mover da VIRTUAL para a
                    // posição; o backend materializa os itens declarados + a entrada.
                    const moved = await api.createWarehouseMovement({
                        lpn_code: state.lpnCode,
                        to_location_code: locationCode,
                        scan_method: 'SCANNED',
                        operator_code: operatorCode || null,
                    });
                    lpn = moved.lpn;
                } else {
                    lpn = await api.intakeWarehouseLpn({
                        lpn_code: state.lpnCode,
                        location_code: locationCode,
                        items: state.items.map((item) => ({
                            product_code: item.product.sku || item.product.ean,
                            qty: Number(item.qty),
                        })),
                        scan_method: 'SCANNED',
                        operator_code: operatorCode || null,
                    });
                }
                PlayMovementDone();
                setState((current) => ({ ...current, phase: 'DONE', done: { lpn, position }, error: null }));
            } catch (error) {
                fail({ title: label('wms.intake.save_failed'), message: error?.message });
            } finally {
                setBusy(false);
            }
        },
        [fail, label, operatorCode, state.declared, state.lpnCode, state.items],
    );

    // ── 4. a posição: bloco do rack abre a torre; não-rack coloca direto ────────
    const handlePositionScan = useCallback(
        async (code) => {
            setBusy(true);
            try {
                const parsed = ParseScanResult(await api.scanWarehouseCode(code));
                if (parsed.type === SCAN_TYPE_BLOCK && parsed.block) {
                    PlayScanSuccess();
                    setState((current) => ({
                        ...current,
                        phase: 'POSITION',
                        block: parsed.block,
                        positions: parsed.positions,
                        error: null,
                    }));
                    return;
                }
                if (parsed.type === SCAN_TYPE_LOCATION && parsed.location) {
                    // Posição NÃO-RACK (doca, staging, fábrica…): o código já é a
                    // posição — coloca direto ali, sem torre.
                    PlayScanSuccess();
                    await placeLpnAt(parsed.location.code, parsed.location);
                    return;
                }
                fail({
                    title: label('wms.intake.not_a_block'),
                    message: label('wms.intake.not_a_block_help'),
                    read: code,
                });
            } catch (error) {
                fail({ title: label('wms.collector.error_generic'), message: error?.message });
            } finally {
                setBusy(false);
            }
        },
        [fail, label, placeLpnAt],
    );

    // ── 5. a posição na torre → grava ───────────────────────────────────────
    const handlePickPosition = useCallback((position) => placeLpnAt(position.code, position), [placeLpnAt]);

    if (state.error) {
        return (
            <FailureScreen
                label={label}
                title={state.error.title}
                message={state.error.message}
                read={state.error.read}
                actions={
                    <BigAction onClick={() => setState((current) => ({ ...current, error: null }))}>
                        {label('wms.collector.try_again')}
                    </BigAction>
                }
            />
        );
    }

    if (state.phase === 'DONE') {
        const placedItems = state.declared
            ? state.declaredItems.map((item) => ({ key: item.product_id, label: item.sku || item.product_id, qty: item.qty }))
            : state.items.map((item) => ({ key: item.key, label: item.product.sku, qty: item.qty }));
        return (
            <SuccessScreen
                title={label('wms.intake.done_title')}
                actions={
                    <>
                        <BigAction onClick={restart}>{label('wms.intake.next_label')}</BigAction>
                        <Button variant="secondary" onClick={onExit} className="w-full">
                            {label('wms.collector.back')}
                        </Button>
                    </>
                }
            >
                <CodeCard caption={label('wms.intake.stored_lpn')} tone="success">
                    {state.done.lpn.code}
                </CodeCard>
                <PlainAddress>{FormatLocationPlain(state.done.position)}</PlainAddress>
                <div className="space-y-1 text-center text-lg text-mono-700 dark:text-mono-300">
                    {placedItems.map((item) => (
                        <p key={item.key}>
                            {item.label} — <Mono>{item.qty}</Mono>
                        </p>
                    ))}
                </div>
            </SuccessScreen>
        );
    }

    // Passos (cosméticos): o fluxo em branco pode repetir produto/qtd (multi-item),
    // então ITEMS aparece na régua entre a quantidade e a posição.
    const steps = state.declared
        ? ['LPN', 'BLOCK', 'POSITION']
        : ['LPN', 'PRODUCT', 'QTY', 'ITEMS', 'BLOCK', 'POSITION'];
    const stepPhase = state.phase === 'PICK_PRODUCT' ? 'PRODUCT' : state.phase;
    const stepIndex = Math.max(0, steps.indexOf(stepPhase));

    return (
        <StepShell
            stepIndex={stepIndex}
            stepCount={steps.length}
            stepName={label(`wms.intake.step_${state.phase.toLowerCase()}`)}
            title={label(`wms.intake.title_${state.phase.toLowerCase()}`)}
            footer={
                <Button variant="secondary" onClick={onExit} disabled={busy} className="w-full">
                    <ArrowLeft size={18} className="mr-2" />
                    {label('wms.collector.back')}
                </Button>
            }
        >
            {state.lpnCode ? <CodeLine>{state.lpnCode}</CodeLine> : null}

            {state.phase === 'LPN' ? (
                <ScanInput label={label} onScan={handleLpnScan} disabled={busy} focusKey="LPN" manualPlaceholder="MP-260814-001" />
            ) : null}

            {state.phase === 'PRODUCT' ? (
                <ScanInput
                    label={label}
                    onScan={handleProductScan}
                    disabled={busy}
                    focusKey="PRODUCT"
                    manualPlaceholder="7891234567890"
                    hint={label('wms.intake.product_hint')}
                />
            ) : null}

            {/* O EMPATE: o mesmo código em dois produtos. A tela pergunta. */}
            {state.phase === 'PICK_PRODUCT' ? (
                <div className="space-y-3">
                    <div className="flex items-start gap-2 rounded-xl border border-warning-300 bg-warning-50 p-3 dark:border-warning-700 dark:bg-warning-950">
                        <Warning size={22} className="mt-0.5 shrink-0 text-warning-600 dark:text-warning-400" />
                        <p className="text-sm text-warning-800 dark:text-warning-200">{label('wms.intake.ambiguous_help')}</p>
                    </div>
                    {state.candidates.map((candidate) => (
                        <BigAction key={candidate.product_id} onClick={() => pickProduct(candidate)}>
                            <span className="block text-left">
                                <Mono className="block text-base">{candidate.sku}</Mono>
                                <span className="block text-sm font-normal opacity-80">{candidate.title}</span>
                            </span>
                        </BigAction>
                    ))}
                </div>
            ) : null}

            {state.phase === 'QTY' ? (
                <div className="space-y-4">
                    <CodeCard caption={state.product.title}>{state.product.sku}</CodeCard>
                    <Input
                        label={label('wms.intake.qty')}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="any"
                        value={state.qty}
                        onChange={(event) => setState((current) => ({ ...current, qty: event.target.value }))}
                        autoFocus
                        className="text-2xl"
                    />
                    <BigAction onClick={confirmQty} disabled={busy}>
                        <Package size={22} className="mr-2" />
                        {label('wms.collector.confirm')}
                    </BigAction>
                </div>
            ) : null}

            {/* Revisão dos itens: adiciona outro ou vai pra posição. */}
            {state.phase === 'ITEMS' ? (
                <div className="space-y-4">
                    <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                        {state.items.map((item) => (
                            <li key={item.key} className="flex items-center gap-2 py-2">
                                <span className="min-w-0 flex-1">
                                    <Mono className="block text-base">{item.product.sku}</Mono>
                                    <span className="block truncate text-sm text-mono-500 dark:text-mono-400">
                                        {item.product.title}
                                    </span>
                                </span>
                                <Mono className="shrink-0 text-lg text-black dark:text-white">{item.qty}</Mono>
                                <button
                                    type="button"
                                    onClick={() => removeItem(item.key)}
                                    disabled={busy}
                                    aria-label={label('wms.intake.remove_item')}
                                    title={label('wms.intake.remove_item')}
                                    className="shrink-0 rounded p-2 transition-colors hover:bg-mono-100 disabled:opacity-50 dark:hover:bg-mono-900"
                                >
                                    <Trash size={16} className="text-danger-600 dark:text-danger-400" />
                                </button>
                            </li>
                        ))}
                    </ul>
                    <Button type="button" variant="secondary" onClick={addAnotherItem} disabled={busy} className="w-full">
                        <Plus size={18} className="mr-2" />
                        {label('wms.intake.add_another')}
                    </Button>
                    <BigAction onClick={goToPosition} disabled={busy || state.items.length === 0}>
                        <Package size={22} className="mr-2" />
                        {label('wms.intake.go_to_position')}
                    </BigAction>
                </div>
            ) : null}

            {state.phase === 'BLOCK' ? (
                <ScanInput label={label} onScan={handlePositionScan} disabled={busy} focusKey="BLOCK" manualPlaceholder="R01-B01" />
            ) : null}

            {state.phase === 'POSITION' ? (
                <BlockTower
                    rack={state.block.rack}
                    bay={state.block.bay}
                    positions={state.positions}
                    onPick={busy ? null : handlePickPosition}
                    pickFilter="FREE"
                    label={label}
                />
            ) : null}
        </StepShell>
    );
};

export default IntakeFlow;
